import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Country, State, City } from 'country-state-city';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import Stepper from '../components/stepper/Stepper';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

// By default, we'll show only India. To show all countries, set this to false
const INDIA_ONLY = true;

function PersonalInformation() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    currentCity: '',
    currentSubject: '',
    currentInstitution: ''
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    mobileNumber: '',
    dateOfBirth: '',
    gender: 'male',
    maritalStatus: '',
    nationality: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: INDIA_ONLY ? 'IN' : '', // Default to India if INDIA_ONLY is true
    availability: '15 Days or less'
  });

  // States for location data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    // Initialize countries
    const allCountries = Country.getAllCountries();
    const filteredCountries = INDIA_ONLY 
      ? allCountries.filter(country => country.isoCode === 'IN')
      : allCountries;
    setCountries(filteredCountries);

    // If INDIA_ONLY is true, initialize states for India
    if (INDIA_ONLY) {
      const indianStates = State.getStatesOfCountry('IN');
      setStates(indianStates);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchProfileInformation();
    }
  }, [user]);

  useEffect(() => {
    // Update states when country changes
    if (formData.country) {
      const countryStates = State.getStatesOfCountry(formData.country);
      setStates(countryStates);
      // Clear state and city when country changes
      if (formData.state) {
        setFormData(prev => ({ ...prev, state: '', city: '' }));
      }
    }
  }, [formData.country]);

  useEffect(() => {
    // Update cities when state changes
    if (formData.country && formData.state) {
      const stateCities = City.getCitiesOfState(formData.country, formData.state);
      setCities(stateCities);
      // Clear city when state changes
      if (formData.city) {
        setFormData(prev => ({ ...prev, city: '' }));
      }
    }
  }, [formData.country, formData.state]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchProfileInformation = async () => {
    try {
      const { data, error } = await supabase
        .from('profile_information')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setFormData(prev => ({
          ...prev,
          mobileNumber: data.mobile_number || '',
          dateOfBirth: data.date_of_birth || '',
          gender: data.gender || 'male',
          maritalStatus: data.marital_status || '',
          nationality: data.nationality || '',
          street: data.street || '',
          city: data.city || '',
          state: data.state || '',
          postalCode: data.postal_code || '',
          country: data.country || '',
          availability: data.availability || '15 Days or less'
        }));
      }
    } catch (error) {
      console.error('Error fetching profile information:', error);
    }
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { data: existingData, error: fetchError } = await supabase
        .from('profile_information')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      const profileData = {
        user_id: user.id,
        mobile_number: formData.mobileNumber,
        date_of_birth: formData.dateOfBirth,
        gender: formData.gender,
        marital_status: formData.maritalStatus,
        nationality: formData.nationality,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: formData.country,
        availability: formData.availability
      };

      let error;
      if (existingData) {
        // Update existing record
        ({ error } = await supabase
          .from('profile_information')
          .update(profileData)
          .eq('id', existingData.id));
      } else {
        // Insert new record
        ({ error } = await supabase
          .from('profile_information')
          .insert([profileData]));
      }

      if (error) throw error;

      toast.success('Personal information saved successfully!');
      navigate('/education-background');
    } catch (error) {
      console.error('Error saving profile information:', error);
      toast.error('Failed to save data. Please try again.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: editForm.firstName,
          last_name: editForm.lastName,
          current_city: editForm.currentCity,
          current_subject: editForm.currentSubject,
          current_institution: editForm.currentInstitution
        })
        .eq('id', user.id);

      if (error) throw error;

      // Update local profile state
      setProfile(prev => ({
        ...prev,
        first_name: editForm.firstName,
        last_name: editForm.lastName,
        current_city: editForm.currentCity,
        current_subject: editForm.currentSubject,
        current_institution: editForm.currentInstitution
      }));

      setShowEditModal(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleProfilePictureChange = async (e) => {
    try {
      setUploading(true);
      const file = e.target.files[0];
      if (!file) return;

      // Validate file type and size
      const fileType = file.type;
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(fileType)) {
        toast.error('Please upload a valid image file (JPEG, PNG, or GIF)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('File size should not exceed 5MB');
        return;
      }

      // Delete old profile picture if exists
      if (profile?.profile_picture_url) {
        const oldPath = profile.profile_picture_url.split('/').pop();
        await supabase.storage
          .from('profile_pictures')
          .remove([`${user.id}/${oldPath}`]);
      }

      // Upload new profile picture
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('profile_pictures')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile_pictures')
        .getPublicUrl(fileName);

      // Update profile with new picture URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_picture_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Update local state
      setProfile(prev => ({ ...prev, profile_picture_url: publicUrl }));
      toast.success('Profile picture updated successfully!');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  const renderProfileSection = () => (
    <div className="flex items-center space-x-4 mb-8">
      <div className="relative">
        <div 
          className="w-16 h-16 md:w-20 md:h-20 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden"
          onClick={() => fileInputRef.current?.click()}
        >
          {profile?.profile_picture_url ? (
            <img 
              src={profile.profile_picture_url} 
              alt="Profile" 
              className="w-full h-full object-cover"
            />
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 md:h-10 md:w-10 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*"
          onChange={handleProfilePictureChange}
          disabled={uploading}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="absolute bottom-0 right-0 bg-primary-500 text-primary-50 rounded-full p-1 hover:bg-primary-600"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        </button>
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-primary-text">
              {profile ? `${profile.first_name} ${profile.last_name}` : 'Loading...'}
            </h2>
            <p className="text-primary-500 text-sm">
              {profile?.current_subject || 'Subject not specified'}
            </p>
            <p className="text-primary-footerText text-sm">
                @{profile?.current_institution || 'Institution not specified'}
            </p>
            <p className="text-primary-footerText text-sm">
              {profile?.current_city || 'Loading...'}
            </p>
            <p className="text-primary-footerText text-sm capitalize">
              Current Profile: {profile?.user_type || 'Loading...'}
            </p>
          </div>
          <button
            onClick={() => {
              setEditForm({
                firstName: profile?.first_name || '',
                lastName: profile?.last_name || '',
                currentCity: profile?.current_city || '',
                currentSubject: profile?.current_subject || '',
                currentInstitution: profile?.current_institution || ''
              });
              setShowEditModal(true);
            }}
            className="text-primary-500 hover:text-primary-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const renderEditModal = () => (
    showEditModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-xl font-semibold mb-4">Edit Profile</h3>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary-text mb-1">
                First Name
              </label>
              <input
                type="text"
                value={editForm.firstName}
                onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                className="w-full rounded-md border border-primary-200 px-3 py-2 focus:border-primary-400 focus:ring-primary-400"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-text mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={editForm.lastName}
                onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                className="w-full rounded-md border border-primary-200 px-3 py-2 focus:border-primary-400 focus:ring-primary-400"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-text mb-1">
                Current City
              </label>
              <input
                type="text"
                value={editForm.currentCity}
                onChange={(e) => setEditForm(prev => ({ ...prev, currentCity: e.target.value }))}
                className="w-full rounded-md border border-primary-200 px-3 py-2 focus:border-primary-400 focus:ring-primary-400"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-text mb-1">
                Current Subject
              </label>
              <input
                type="text"
                value={editForm.currentSubject}
                onChange={(e) => setEditForm(prev => ({ ...prev, currentSubject: e.target.value }))}
                placeholder="e.g. Mathematics, Science, English"
                className="w-full rounded-md border border-primary-200 px-3 py-2 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-text mb-1">
                Current Institution
              </label>
              <input
                type="text"
                value={editForm.currentInstitution}
                onChange={(e) => setEditForm(prev => ({ ...prev, currentInstitution: e.target.value }))}
                placeholder="e.g. ABC School"
                className="w-full rounded-md border border-primary-200 px-3 py-2 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-primary-200 rounded-md text-primary-text hover:bg-primary-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-500 text-primary-50 rounded-md hover:bg-opacity-90"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );

  return (
    <div className="min-h-screen flex flex-col bg-primary-50">
      <Navigation />
      <div className="flex-grow pt-16 md:pt-20 mt-2 md:mt-5 content-center mb-4">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          {/* Mobile Stepper */}
          <div className="block md:hidden mb-6">
            <Stepper />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
            {/* Desktop Stepper */}
            <div className="hidden md:block md:col-span-3">
              <div className="sticky top-24">
                <Stepper />
              </div>
            </div>

            {/* Form Section */}
            <div className="col-span-1 md:col-span-9">
              <div className="bg-primary-extra rounded-lg shadow-sm p-4 md:p-8">
                {renderProfileSection()}
                <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Email ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        readOnly
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Mobile Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        placeholder="Add Mobile Number"
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Gender <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1 flex flex-wrap gap-4">
                        {['Male', 'Female'].map((option) => (
                          <label key={option} className="inline-flex items-center">
                            <input
                              type="radio"
                              name="gender"
                              value={option.toLowerCase()}
                              checked={formData.gender === option.toLowerCase()}
                              onChange={handleInputChange}
                              className="form-radio text-primary-500"
                              required
                            />
                            <span className="ml-2 text-sm">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Marital Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="maritalStatus"
                        value={formData.maritalStatus}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      >
                        <option value="">Please Select</option>
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Nationality <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="nationality"
                        value={formData.nationality}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      >
                        <option value="">Please Select</option>
                        <option value="indian">Indian</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="street"
                        value={formData.street}
                        onChange={handleInputChange}
                        placeholder="Add Address"
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Postal Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="Enter Postal code"
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.isoCode} value={country.isoCode}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        State <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                        disabled={!formData.country}
                      >
                        <option value="">Select State</option>
                        {states.map(state => (
                          <option key={state.isoCode} value={state.isoCode}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        City <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                        disabled={!formData.state}
                      >
                        <option value="">Select City</option>
                        {cities.map(city => (
                          <option key={city.name} value={city.name}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary-text mb-2">
                      Availability to join <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2 md:gap-3">
                      {['15 Days or less', '1 Month', '2 Month', '3 Month', 'More than 3 Months'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, availability: option }))}
                          className={`px-3 md:px-4 py-2 rounded-full text-sm ${
                            formData.availability === option
                              ? 'bg-primary-50 text-gray-800'
                              : 'bg-white text-primary-text border border-primary-200'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 md:space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="px-4 md:px-6 py-2 md:py-2.5 border border-primary-200 rounded-md text-primary-text hover:bg-primary-100 text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 md:px-6 py-2 md:py-2.5 bg-primary-500 text-primary-50  rounded-md hover:bg-opacity-90 text-sm"
                    >
                      Save & Next
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderEditModal()}
      <Footer />
    </div>
  );
}

export default PersonalInformation;